import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { isRef, toRaw, unref } from 'vue'

export type UploadState = 'idle' | 'queued' | 'uploading' | 'done' | 'failed'
export type UploadDeviceClass = 'desktop' | 'tablet' | 'mobile' | 'unknown'

export type ImagePresetSizeGeometry = {
    shape?: 'rect' | 'rounded' | 'circle' | 'hexagon' | 'custom'
    cornerRadiusMm?: number
    bleedMm?: number
    safeMarginMm?: number
    dpi?: number
    trimWidthMm?: number
    trimHeightMm?: number
    fullWidthMm?: number
    fullHeightMm?: number
    shapeParams?: Record<string, unknown> | null
}

export type ImagePreset = {
    productId?: number
    sizeId?: string
    size?: {
        id?: string | null
        variantId?: string | null
        title?: string | null
        /** Endformat in mm (ohne Beschnitt) */
        widthMm?: number
        heightMm?: number
        /** Beschnitt/Sicherheitsbereich und Form aus dem Bootstrap (Design-Editor Phase 1) */
        bleedMm?: number
        safeMarginMm?: number
        geometry?: ImagePresetSizeGeometry | null
    }
    options?: Array<{ id: string; value: string }>
    grayscale?: boolean
    /** true => edits.crop deckt Endformat + 2 x Beschnitt ab (Fulfillment rechnet damit) */
    bleedIncluded?: boolean
}

export type ImageEdits = {
    crop?: {
        x: number
        y: number
        w: number
        h: number
        nx?: number
        ny?: number
        nw?: number
        nh?: number
        sourceWidth?: number
        sourceHeight?: number
    }
    rotate?: number
    filters?: Record<string, any>
    preset?: ImagePreset
    swapAR?: boolean
    orientMode?: 'auto' | 'manual'
    lockAR?: boolean
}

export type ImageRecord = {
    id: string
    assetId?: string | null
    name: string
    createdAt: number
    updatedAt: number
    mime: string
    bytes: number
    width?: number
    height?: number
    originalBlob?: Blob
    workingBlob: Blob
    editorBlob?: Blob
    editorWidth?: number
    editorHeight?: number
    thumbBlob?: Blob
    thumbWidth?: number
    thumbHeight?: number
    edits?: ImageEdits
    /** Tags/Alben (z. B. "Kindergarten", "Hochzeit"); Server: meta.tags */
    tags?: string[]
    productId?: number | null
    categoryId?: number | null
    projectCode?: string | null
    upload?: {
        state: UploadState
        progressPct?: number
        uploadedBytes?: number
        totalBytes?: number
        phase?: 'queued' | 'presigning' | 'uploading' | 'completing' | 'done' | 'failed'
        previewUrl?: string
        thumbUrl?: string
        fullscreenPreviewUrl?: string
        remoteUrl?: string
        source?: 'desktop' | 'phone' | 'stock'
        deviceClass?: UploadDeviceClass
        deviceOs?: string
        deviceBrowser?: string
        editorReadyAt?: number
        lastError?: string
        tries?: number
        updatedAt?: number
    }
}

type ImageDbSchema = DBSchema & {
    records: {
        key: string
        value: ImageRecord
        indexes: {
            'by-updatedAt': number
            'by-categoryId': number
            'by-productId': number
            'by-projectCode': string
            'by-uploadState': string
        }
    }
}

type MemoryStore = {
    records: Map<string, ImageRecord>
}

const memoryStores = new Map<string, MemoryStore>()
const indexedDbPromises = new Map<string, Promise<IDBPDatabase<ImageDbSchema> | null>>()

function getMemoryStore(dbName = 'imageDB'): MemoryStore {
    let store = memoryStores.get(dbName)
    if (!store) {
        store = { records: new Map<string, ImageRecord>() }
        memoryStores.set(dbName, store)
    }
    return store
}

function canUseIndexedDb() {
    return typeof indexedDB !== 'undefined'
}

function toPlainCloneable<T>(input: T): T {
    const value: any = isRef(input) ? unref(input) : input
    if (value == null || typeof value !== 'object') return value

    if (
        value instanceof Blob ||
        (typeof File !== 'undefined' && value instanceof File) ||
        value instanceof ArrayBuffer
    ) {
        return value
    }

    if (ArrayBuffer.isView(value)) return value

    const raw = toRaw(value)
    if (Array.isArray(raw)) return raw.map((entry) => toPlainCloneable(entry)) as any

    const out: any = {}
    for (const key of Object.keys(raw)) out[key] = toPlainCloneable((raw as any)[key])
    return out
}

function cloneRecord<T extends ImageRecord | Partial<ImageRecord> | null | undefined>(input: T): T {
    if (!input) return input
    return toPlainCloneable(input) as T
}

function isLikelyDesktopRuntime(record?: Partial<ImageRecord> | null) {
    const explicit = String((record as any)?.upload?.deviceClass || '').trim().toLowerCase()
    if (explicit === 'desktop') return true
    if (explicit === 'mobile' || explicit === 'tablet') return false

    if (typeof navigator !== 'undefined') {
        const ua = String(navigator.userAgent || '')
        if (/Android|iPhone|iPad|iPod/i.test(ua)) return false
    }
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        return !window.matchMedia('(max-width: 820px)').matches
    }
    return true
}

function normalizeStoredRecord(record: ImageRecord): ImageRecord {
    const next = cloneRecord(record) as ImageRecord
    if (next.originalBlob && next.workingBlob && next.originalBlob === next.workingBlob) {
        next.originalBlob = undefined
    }
    return next
}

function toIndexedDbRecord(record: ImageRecord): ImageRecord {
    const next = normalizeStoredRecord(record)
    const keepHeavyBlobs = !isLikelyDesktopRuntime(next)
    if (!keepHeavyBlobs) {
        next.originalBlob = undefined
        next.workingBlob = undefined as any
        next.editorBlob = undefined
        next.thumbBlob = undefined
        return next
    }

    if (next.originalBlob && next.workingBlob && next.originalBlob === next.workingBlob) {
        next.originalBlob = undefined
    }
    return next
}

function mergeRecord(existing: ImageRecord, patch: Partial<ImageRecord>): ImageRecord {
    const next = {
        ...existing,
        ...patch,
        upload: patch.upload
            ? {
                ...(existing.upload || {}),
                ...patch.upload,
            }
            : existing.upload,
        edits: patch.edits
            ? {
                ...(existing.edits || {}),
                ...patch.edits,
            }
            : existing.edits,
        updatedAt: Date.now(),
    } as ImageRecord
    return cloneRecord(next) as ImageRecord
}

function sortByUpdatedAtDesc(records: ImageRecord[]) {
    return [...records].sort((a, b) => Number(b?.updatedAt || b?.createdAt || 0) - Number(a?.updatedAt || a?.createdAt || 0))
}

async function getIndexedDb(dbName = 'imageDB') {
    if (!canUseIndexedDb()) return null
    let promise = indexedDbPromises.get(dbName)
    if (!promise) {
        promise = openDB<ImageDbSchema>(dbName, 1, {
            upgrade(db) {
                if (db.objectStoreNames.contains('records')) return
                const store = db.createObjectStore('records', { keyPath: 'id' })
                store.createIndex('by-updatedAt', 'updatedAt')
                store.createIndex('by-categoryId', 'categoryId')
                store.createIndex('by-productId', 'productId')
                store.createIndex('by-projectCode', 'projectCode')
                store.createIndex('by-uploadState', 'upload.state')
            },
        }).catch(() => null)
        indexedDbPromises.set(dbName, promise)
    }
    return await promise
}

async function getAllRecords(dbName = 'imageDB') {
    const memory = getMemoryStore(dbName)
    const db = await getIndexedDb(dbName)
    if (db) {
        const rows = await db.getAll('records')
        const merged = new Map<string, ImageRecord>()
        // Store raw refs here and clone once on output below — cloning into the map
        // *and* on output deep-copied every record twice per call (costly at 150+).
        for (const rec of rows) {
            const id = String(rec?.id || '').trim()
            if (!id) continue
            merged.set(id, rec as ImageRecord)
        }
        for (const [id, rec] of memory.records.entries()) {
            merged.set(String(id), rec as ImageRecord)
        }
        return sortByUpdatedAtDesc(Array.from(merged.values())).map((rec) => cloneRecord(rec) as ImageRecord)
    }
    return sortByUpdatedAtDesc(Array.from(memory.records.values())).map((rec) => cloneRecord(rec) as ImageRecord)
}

/**
 * Indexed query that only deserializes the matching rows (instead of loading the
 * whole store + blobs via getAll). The IndexedDB index is used as a fast prefilter
 * and the in-memory overlay is applied with `matches` so the result is identical to
 * filtering getAllRecords() — memory wins (it is fresher and, on desktop, still
 * holds the blobs stripped from the IndexedDB copy); a record whose memory value no
 * longer matches is excluded.
 */
async function getRecordsByIndex(
    dbName: string,
    indexName: 'by-uploadState' | 'by-projectCode',
    indexKey: string,
    matches: (rec: ImageRecord) => boolean,
) {
    const memory = getMemoryStore(dbName)
    const db = await getIndexedDb(dbName)
    if (db) {
        const rows = await db.getAllFromIndex('records', indexName, indexKey)
        const merged = new Map<string, ImageRecord>()
        for (const rec of rows) {
            const id = String(rec?.id || '').trim()
            if (!id) continue
            merged.set(id, rec as ImageRecord)
        }
        for (const [idRaw, rec] of memory.records.entries()) {
            const id = String(idRaw)
            if (matches(rec)) merged.set(id, rec)
            else merged.delete(id)
        }
        return sortByUpdatedAtDesc(Array.from(merged.values())).map((rec) => cloneRecord(rec) as ImageRecord)
    }
    return sortByUpdatedAtDesc(Array.from(memory.records.values()).filter(matches)).map(
        (rec) => cloneRecord(rec) as ImageRecord,
    )
}

async function readRecord(dbName: string, id: string) {
    const wanted = String(id || '').trim()
    if (!wanted) return null
    const memory = getMemoryStore(dbName).records.get(wanted)
    if (memory) return cloneRecord(memory)
    const db = await getIndexedDb(dbName)
    if (db) return cloneRecord(await db.get('records', wanted))
    return null
}

async function writeRecord(dbName: string, record: ImageRecord) {
    const safe = normalizeStoredRecord(record)
    getMemoryStore(dbName).records.set(String(safe.id), cloneRecord(safe) as ImageRecord)
    const db = await getIndexedDb(dbName)
    if (db) {
        await db.put('records', toIndexedDbRecord(safe))
    }
    return safe.id
}

async function deleteRecord(dbName: string, id: string) {
    const wanted = String(id || '').trim()
    if (!wanted) return
    getMemoryStore(dbName).records.delete(wanted)
    const db = await getIndexedDb(dbName)
    if (db) {
        await db.delete('records', wanted)
    }
}

export async function resetImageDB(dbName = 'imageDB') {
    const db = await getIndexedDb(dbName)
    if (db) {
        await db.clear('records')
    }
    getMemoryStore(dbName).records.clear()
}

export function useImageDB(dbName = 'imageDB') {
    return {
        async put(image: ImageRecord) {
            return await writeRecord(dbName, image)
        },

        async get(id: string) {
            return await readRecord(dbName, id)
        },

        async delete(id: string) {
            await deleteRecord(dbName, id)
        },

        async getAllKeys() {
            const db = await getIndexedDb(dbName)
            if (db) return await db.getAllKeys('records') as string[]
            return Array.from(getMemoryStore(dbName).records.keys())
        },

        async getAll() {
            return await getAllRecords(dbName)
        },

        async getByCategory(categoryId: number) {
            const wanted = Number(categoryId || 0)
            const records = await getAllRecords(dbName)
            return records.filter((rec) => Number(rec?.categoryId || 0) === wanted)
        },

        async getByProduct(productId: number) {
            const wanted = Number(productId || 0)
            const records = await getAllRecords(dbName)
            return records.filter((rec) => Number(rec?.productId || 0) === wanted)
        },

        async getByCategoryProduct(categoryId: number, productId: number) {
            const wantedCategory = Number(categoryId || 0)
            const wantedProduct = Number(productId || 0)
            const records = await getAllRecords(dbName)
            return records.filter((rec) =>
                Number(rec?.categoryId || 0) === wantedCategory &&
                Number(rec?.productId || 0) === wantedProduct,
            )
        },

        async deleteByCategoryProduct(categoryId: number, productId: number) {
            const wantedCategory = Number(categoryId || 0)
            const wantedProduct = Number(productId || 0)
            const rows = (await getAllRecords(dbName)).filter((rec) =>
                Number(rec?.categoryId || 0) === wantedCategory &&
                Number(rec?.productId || 0) === wantedProduct,
            )
            await Promise.all(rows.map((rec) => deleteRecord(dbName, rec.id)))
        },

        async getByProjectCode(code: string) {
            // Indexed fast path (by-projectCode) instead of loading the whole store.
            // Used on reload/editor-hydration, which is the heavy case for many images.
            const wanted = String(code || '').trim()
            return await getRecordsByIndex(
                dbName,
                'by-projectCode',
                wanted,
                (rec) => String(rec?.projectCode || '').trim() === wanted,
            )
        },

        async getByUploadState(state: UploadState) {
            // Indexed fast path (by-uploadState) instead of loading the whole store.
            const wanted = String(state || '').trim()
            return await getRecordsByIndex(
                dbName,
                'by-uploadState',
                wanted,
                (rec) => String(rec?.upload?.state || '').trim() === wanted,
            )
        },

        async update(id: string, patch: Partial<ImageRecord>) {
            const wanted = String(id || '').trim()
            if (!wanted) return null
            const existing = await readRecord(dbName, wanted)
            if (!existing) return null
            const safePatch = cloneRecord(patch) as Partial<ImageRecord>
            const next = mergeRecord(existing, safePatch)
            await writeRecord(dbName, next)
            return cloneRecord(next)
        },
    }
}
